// Vistoria por link público (pedido 2026-10-23) — o motorista abre o link
// fixo /vistoria, digita o código que a empresa mandou (WhatsApp) e faz a
// vistoria pelo navegador, SEM conta (bypass em __root.tsx; o código é a
// credencial). Mesmo checklist da empresa do app (Gestão de Frota →
// Vistorias → Checklist). Um código = uma vistoria; as fotos sobem logo
// depois pelo mesmo código.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, ClipboardCheck, Loader2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { compressImageFile } from "@/lib/compress-image";
import { stampImageFile } from "@/lib/stamp-image";
import { mapWithConcurrency } from "@/lib/concurrency";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { PhotoTile, Section, STATUS_BUTTONS } from "@/components/mobile-vehicle-inspection";
import {
  DRIVER_SELFIE_PHOTO,
  FUEL_LEVEL_LABELS,
  MAX_INSPECTION_PHOTOS,
  publicInspectionService,
  type FuelLevel,
  type InspectionChecklistItem,
  type PublicInspectionView,
} from "@/services/gpsdrive-vehicle-inspections.service";

export const Route = createFileRoute("/vistoria")({
  component: PublicInspectionPage,
  head: () => ({ meta: [{ title: "Vistoria do veículo" }, { name: "robots", content: "noindex" }] }),
});

const CODE_KEY = "gd_public_inspection_code";
const dateTimeFmt = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const errMsg = (e: unknown, fallback: string) =>
  e instanceof GpsDriveApiError ? e.message : e instanceof Error && e.message ? e.message : fallback;

function PublicInspectionPage() {
  const [code, setCode] = useState("");
  const [view, setView] = useState<PublicInspectionView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ failedPhotos: number } | null>(null);

  const load = async (raw: string) => {
    const c = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (c.length < 4) {
      setError("Digite o código que a empresa mandou.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const v = await publicInspectionService.get(c);
      setView(v);
      try {
        sessionStorage.setItem(CODE_KEY, c);
      } catch {
        // sem sessionStorage — só não lembra ao recarregar
      }
    } catch (e) {
      setError(errMsg(e, "Não foi possível abrir. Confira sua internet e tente de novo."));
    } finally {
      setLoading(false);
    }
  };

  // ?codigo=ABC123 no link ou código digitado antes de recarregar a página.
  useEffect(() => {
    let initial = new URLSearchParams(window.location.search).get("codigo") ?? "";
    if (!initial) {
      try {
        initial = sessionStorage.getItem(CODE_KEY) ?? "";
      } catch {
        initial = "";
      }
    }
    if (initial) {
      setCode(initial.toUpperCase());
      void load(initial);
    }
  }, []);

  const reset = () => {
    try {
      sessionStorage.removeItem(CODE_KEY);
    } catch {
      // ignora
    }
    setView(null);
    setDone(null);
    setCode("");
    setError(null);
  };

  return (
    <div className="min-h-dvh bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <header className="border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto flex max-w-xl items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-primary" />
          <div className="min-w-0">
            <div className="truncate text-[15px] font-bold">Vistoria do veículo</div>
            {view?.companyName && <div className="truncate text-xs text-slate-500">{view.companyName}</div>}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-4 pb-16 pt-4">
        {done || (view?.used && view.inspection) ? (
          <div className="mt-10 flex flex-col items-center gap-3 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-500" />
            <h1 className="text-xl font-bold">Vistoria enviada!</h1>
            <p className="text-sm text-slate-500">
              Obrigado{view ? `, ${view.driverName.split(" ")[0]}` : ""}. A empresa já recebeu a vistoria
              {view ? ` do veículo ${view.vehicleName}` : ""}.
            </p>
            {done && done.failedPhotos > 0 && (
              <p className="text-sm text-amber-600">{done.failedPhotos} foto(s) não foram enviadas.</p>
            )}
            <button type="button" onClick={reset} className="mt-2 text-sm font-medium text-primary underline-offset-2 hover:underline">
              Usar outro código
            </button>
          </div>
        ) : view ? (
          <PublicInspectionForm view={view} onDone={(failedPhotos) => setDone({ failedPhotos })} onReset={reset} />
        ) : (
          <div className="mt-8 space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Digite o código que a empresa mandou para você no WhatsApp.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void load(code);
              }}
              className="space-y-3"
            >
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                autoCapitalize="characters"
                autoComplete="one-time-code"
                placeholder="CÓDIGO"
                maxLength={12}
                className="h-14 w-full rounded-xl border border-slate-300 bg-white text-center font-mono text-2xl font-bold tracking-[0.3em] dark:border-slate-700 dark:bg-slate-900"
              />
              {error && <p className="text-sm text-rose-600">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-bold text-primary-foreground disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Começar vistoria
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}

interface PhotoDraft {
  id: string;
  slot: string;
  label: string;
  file: File;
  preview: string;
  takenAt: Date;
  // Carimbo/compressão começa assim que a foto é tirada (2026-10-23, envio
  // demorando) — no "Enviar" só falta subir.
  ready: Promise<File>;
}

function PublicInspectionForm({
  view,
  onDone,
  onReset,
}: {
  view: PublicInspectionView;
  onDone: (failedPhotos: number) => void;
  onReset: () => void;
}) {
  const template = view.template;
  const [odometer, setOdometer] = useState("");
  const [fuel, setFuel] = useState<FuelLevel | null>(null);
  const [items, setItems] = useState<InspectionChecklistItem[]>(() =>
    template.items.map((i) => ({ key: i.key, label: i.label, status: "ok" as const })),
  );
  const photoModeByKey = useMemo(() => new Map(template.items.map((i) => [i.key, i.photo])), [template]);
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState<PhotoDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selfieInputRef = useRef<HTMLInputElement>(null);
  const selfieSlot = template.photos.find((p) => p.key === DRIVER_SELFIE_PHOTO.key) ?? null;
  const vehicleSlots = template.photos.filter((p) => p.key !== DRIVER_SELFIE_PHOTO.key);
  const pendingRef = useRef({ slot: "", label: "" });
  const vehicleLabel = `${view.vehicleName}${view.plate && view.plate !== view.vehicleName ? ` · ${view.plate}` : ""}`;

  const setItem = (key: string, patch: Partial<InspectionChecklistItem>) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  const pickPhoto = (slot: string, label: string) => {
    pendingRef.current = { slot, label };
    (slot === `slot:${DRIVER_SELFIE_PHOTO.key}` ? selfieInputRef : fileInputRef).current?.click();
  };
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const { slot, label } = pendingRef.current;
    const takenAt = new Date();
    const ready = stampImageFile(file, [`${dateTimeFmt.format(takenAt)} · ${vehicleLabel}`, `Motorista: ${view.driverName}`])
      .then((f) => f ?? compressImageFile(file))
      .catch(() => file);
    setPhotos((prev) => [
      ...prev.filter((p) => slot === "extra" || p.slot !== slot),
      { id: crypto.randomUUID(), slot, label, file, preview: URL.createObjectURL(file), takenAt, ready },
    ]);
  };
  const removePhoto = (id: string) => setPhotos((prev) => prev.filter((p) => p.id !== id));
  const photoFor = (slot: string) => photos.find((p) => p.slot === slot);
  const extras = photos.filter((p) => p.slot === "extra");

  const submit = async () => {
    setError(null);
    if (template.requireOdometer && !odometer.trim()) return setError("Informe o km do painel.");
    if (template.requireFuel && !fuel) return setError("Informe o nível de combustível.");
    const missingNote = items.find((i) => i.status === "problema" && !i.note?.trim());
    if (missingNote) return setError(`Descreva o problema em "${missingNote.label}".`);
    const missingItemPhoto = items.find(
      (i) => i.status !== "na" && photoModeByKey.get(i.key) === "required" && !photoFor(`item:${i.key}`),
    );
    if (missingItemPhoto) return setError(`Tire a foto de "${missingItemPhoto.label}".`);
    const missingSlot = template.photos.find((p) => p.required && !photoFor(`slot:${p.key}`));
    if (missingSlot) return setError(`Tire a foto "${missingSlot.label}".`);

    setSaving(true);
    try {
      setProgress("Enviando vistoria...");
      const checklist = items.map((i) => (i.status === "problema" ? i : { key: i.key, label: i.label, status: i.status }));
      await publicInspectionService.submit(view.code, {
        odometerKm: odometer.trim() ? Math.round(Number(odometer.replace(/\D/g, ""))) : null,
        fuelLevel: fuel,
        checklist,
        notes: notes.trim() || null,
        performedAt: new Date().toISOString(),
      });
      // 3 fotos subindo ao mesmo tempo.
      let failed = 0;
      let sent = 0;
      if (photos.length > 0) setProgress(`Enviando fotos 0 de ${photos.length}...`);
      await mapWithConcurrency(photos, 3, async (p) => {
        try {
          await publicInspectionService.uploadPhoto(view.code, await p.ready, p.label);
        } catch {
          failed += 1;
        }
        sent += 1;
        setProgress(`Enviando fotos ${sent} de ${photos.length}...`);
      });
      onDone(failed);
    } catch (e) {
      setError(errMsg(e, "Não foi possível enviar. Confira sua internet e tente de novo."));
    } finally {
      setSaving(false);
      setProgress("");
    }
  };

  return (
    <div className="space-y-4">
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />
      <input ref={selfieInputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} />

      <Section title="Veículo">
        <div className="text-[15px] font-semibold">{vehicleLabel}</div>
        <div className="mt-0.5 text-xs text-slate-500">
          Motorista: {view.driverName} ·{" "}
          <button type="button" onClick={onReset} className="text-primary underline-offset-2 hover:underline">
            trocar código
          </button>
        </div>
      </Section>

      <Section title="Quilometragem e combustível">
        <label className="block text-[12px] font-medium text-slate-500 dark:text-slate-400">
          Km do painel{template.requireOdometer ? " *" : ""}
          <input
            inputMode="numeric"
            value={odometer}
            onChange={(e) => setOdometer(e.target.value.replace(/\D/g, ""))}
            placeholder="Ex.: 45230"
            className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </label>
        <div className="mt-3 text-[12px] font-medium text-slate-500 dark:text-slate-400">
          Combustível{template.requireFuel ? " *" : ""}
        </div>
        <div className="mt-1 grid grid-cols-5 gap-1">
          {(Object.keys(FUEL_LEVEL_LABELS) as FuelLevel[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFuel(fuel === f ? null : f)}
              className={cn(
                "h-10 rounded-lg text-[12px] font-semibold",
                fuel === f ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
              )}
            >
              {FUEL_LEVEL_LABELS[f]}
            </button>
          ))}
        </div>
      </Section>

      {selfieSlot && (
        <Section title="Foto ponto *">
          <div className="flex items-center gap-3">
            <div className="w-28 shrink-0">
              <PhotoTile
                label="Selfie"
                preview={photoFor(`slot:${selfieSlot.key}`)?.preview}
                onPick={() => pickPhoto(`slot:${selfieSlot.key}`, selfieSlot.label)}
                onRemove={(() => {
                  const p = photoFor(`slot:${selfieSlot.key}`);
                  return p ? () => removePhoto(p.id) : undefined;
                })()}
              />
            </div>
            <p className="text-[13px] leading-snug text-slate-600 dark:text-slate-300">
              Tire uma foto sua <b>mostrando a placa do veículo</b>.
            </p>
          </div>
        </Section>
      )}

      <Section title="Checklist">
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((item) => {
            const photoMode = photoModeByKey.get(item.key) ?? "none";
            const itemPhoto = photoFor(`item:${item.key}`);
            return (
              <div key={item.key} className="py-2.5">
                <div className="flex items-center gap-2">
                  <div className="flex-1 text-[13px] text-slate-800 dark:text-slate-100">
                    {item.label}
                    {photoMode === "required" && item.status !== "na" && (
                      <span className="ml-1 text-[11px] font-semibold text-rose-500">foto *</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {STATUS_BUTTONS.map((b) => (
                      <button
                        key={b.value}
                        type="button"
                        onClick={() => setItem(item.key, { status: b.value })}
                        className={cn(
                          "h-8 rounded-md px-2 text-[11px] font-semibold",
                          item.status === b.value ? b.active : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
                        )}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>
                {item.status === "problema" && (
                  <input
                    value={item.note ?? ""}
                    onChange={(e) => setItem(item.key, { note: e.target.value })}
                    placeholder="O que está errado?"
                    className="mt-2 h-10 w-full rounded-lg border border-amber-300 bg-amber-50 px-3 text-sm text-slate-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-slate-100"
                  />
                )}
                {photoMode !== "none" && item.status !== "na" && (
                  <div className="mt-2 w-24">
                    <PhotoTile
                      label="Foto"
                      preview={itemPhoto?.preview}
                      onPick={() => pickPhoto(`item:${item.key}`, item.label)}
                      onRemove={itemPhoto ? () => removePhoto(itemPhoto.id) : undefined}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      <Section title={vehicleSlots.length > 0 ? "Fotos" : "Outra foto (opcional)"}>
        <div className="grid grid-cols-3 gap-2">
          {vehicleSlots.map((slot) => {
            const p = photoFor(`slot:${slot.key}`);
            return (
              <PhotoTile
                key={slot.key}
                label={slot.required ? `${slot.label} *` : slot.label}
                preview={p?.preview}
                onPick={() => pickPhoto(`slot:${slot.key}`, slot.label)}
                onRemove={p ? () => removePhoto(p.id) : undefined}
              />
            );
          })}
          {extras.map((p, n) => (
            <PhotoTile key={p.id} label={`Extra ${n + 1}`} preview={p.preview} onPick={() => undefined} onRemove={() => removePhoto(p.id)} />
          ))}
          {photos.length < MAX_INSPECTION_PHOTOS && (
            <button
              type="button"
              onClick={() => pickPhoto("extra", "Extra")}
              className="flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 text-[11px] font-medium text-slate-500 dark:border-slate-700 dark:text-slate-400"
            >
              <Plus className="h-5 w-5" />
              Outra foto
            </button>
          )}
        </div>
      </Section>

      <Section title="Observações">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Algo mais que a empresa precisa saber?"
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        />
      </Section>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={() => void submit()}
        disabled={saving}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-bold text-primary-foreground shadow-sm disabled:opacity-60"
      >
        {saving ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {progress || "Enviando..."}
          </>
        ) : (
          "Enviar vistoria"
        )}
      </button>
    </div>
  );
}
